/**
 * Sets up Related Papers carousel with progressive enhancement:
 * - Only active on mobile breakpoint (< 568px)
 * - Uses modern CSS ::scroll-button() where supported
 * - Falls back to JavaScript navigation buttons on older browsers
 * - Provides touch-friendly scrolling on mobile devices
 * - On desktop (≥ 568px), defers to setUpViewMoreRelatedWorks
 */

// Breakpoint must match design_system/_breakpoint.scss
const MOBILE_MAX_WIDTH = 567;

const isMobileBreakpoint = () => window.innerWidth <= MOBILE_MAX_WIDTH;

// WeakMap to store AbortControllers per carousel element
const carouselControllers = new WeakMap<Element, AbortController>();

export const setUpRelatedPapersCarousel = () => {
  const carousels = [
    ...document.querySelectorAll('.js-related-papers-carousel'),
  ];

  carousels.forEach((carousel) => {
    // Create AbortController for this carousel instance
    const controller = new AbortController();
    const { signal } = controller;

    // Store controller for potential manual cleanup
    carouselControllers.set(carousel, controller);

    const slidesContainer = carousel.querySelector('.js-slides-container');
    if (!slidesContainer) return;

    const slides = slidesContainer.children;
    if (!slides || slides.length === 0) return;

    // Check if browser supports CSS scroll buttons
    // If supported, we don't need to show JavaScript fallback buttons
    const supportsScrollButton = CSS.supports('anchor-name', '--test');

    if (supportsScrollButton) {
      return; // Modern CSS will handle it
    }

    // Fallback: Set up JavaScript navigation buttons (only on mobile)
    const prevButtonContainer = carousel.querySelector(
      '.js-prev-button-container',
    );
    const nextButtonContainer = carousel.querySelector(
      '.js-next-button-container',
    );
    if (!prevButtonContainer || !nextButtonContainer) return;

    const prevButton: HTMLButtonElement | null =
      prevButtonContainer?.querySelector('button');
    const nextButton: HTMLButtonElement | null =
      nextButtonContainer?.querySelector('button');

    if (prevButton === null || nextButton === null) {
      return;
    }

    let currentSlideIndex = 0;

    function updateButtonVisibility() {
      // Only show carousel navigation on mobile breakpoint
      // Desktop uses setUpViewMoreRelatedWorks instead
      if (!isMobileBreakpoint()) {
        prevButtonContainer?.classList.add('hidden');
        nextButtonContainer?.classList.add('hidden');
        return;
      }

      // Show/hide prev button based on current position
      if (currentSlideIndex === 0) {
        prevButtonContainer?.classList.add('hidden');
      } else {
        prevButtonContainer?.classList.remove('hidden');
      }

      // Check if we're at the end of the carousel
      const lastSlide = slides[slides.length - 1] as HTMLElement;
      const lastSlideRect = lastSlide.getBoundingClientRect();
      const containerRect = (
        slidesContainer as HTMLElement
      ).getBoundingClientRect();

      if (lastSlideRect.right > containerRect.right + 10) {
        nextButtonContainer?.classList.remove('hidden');
      } else {
        nextButtonContainer?.classList.add('hidden');
      }
    }

    function scrollToSlide(index: number) {
      const slide: HTMLElement | null = slides[index] as HTMLElement;
      if (slide === null) {
        return;
      }

      slide.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });

      // Wait for scroll animation to complete, then update button visibility
      let lastPosition: number;
      let unchangedFrames = 0;

      function checkScrollComplete() {
        const newPosition = slide?.getBoundingClientRect().left;

        if (newPosition === lastPosition) {
          unchangedFrames++;
          if (unchangedFrames > 2) {
            updateButtonVisibility();
            return;
          }
        } else {
          unchangedFrames = 0;
          lastPosition = newPosition ?? 0;
        }

        requestAnimationFrame(checkScrollComplete);
      }

      requestAnimationFrame(checkScrollComplete);
    }

    function handlePrevClick() {
      if (currentSlideIndex > 0) {
        currentSlideIndex--;
        scrollToSlide(currentSlideIndex);
      }
    }

    function handleNextClick() {
      if (currentSlideIndex < slides.length - 1) {
        currentSlideIndex++;
        scrollToSlide(currentSlideIndex);
      }
    }

    // Set up event listeners with signal for auto-cleanup
    prevButton.addEventListener('click', handlePrevClick, { signal });
    nextButton.addEventListener('click', handleNextClick, { signal });

    // Handle window resize - hide buttons if switching to desktop
    let resizeTimeout: ReturnType<typeof setTimeout>;
    window.addEventListener(
      'resize',
      () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(updateButtonVisibility, 150);
      },
      { signal },
    );

    // Initial button visibility setup
    updateButtonVisibility();
  });
};

export const setUpRelatedPapersCarouselCardClick = () => {
  document
    .querySelectorAll('.js-related-papers-carousel-card')
    .forEach((card) => {
      card.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;

        // If the click is already on an interactive element, let it handle itself
        if (target.closest('a') || target.closest('button')) {
          return;
        }

        // Navigate to the paper URL via the first <a> link in the card (title link)
        const titleLink = card.querySelector('a') as HTMLAnchorElement | null;
        if (titleLink) {
          window.location.href = titleLink.href;
        }
      });
    });
};

// Export cleanup function for manual cleanup if needed
export const cleanupRelatedPapersCarousel = () => {
  const carousels = [
    ...document.querySelectorAll('.js-related-papers-carousel'),
  ];

  carousels.forEach((carousel) => {
    const controller = carouselControllers.get(carousel);
    if (controller) {
      controller.abort();
      carouselControllers.delete(carousel);
    }
  });
};
